import { supabase } from '@/integrations/supabase/client';

interface ErrorEvent {
  error: Error;
  errorInfo?: React.ErrorInfo;
  context?: string;
  userAgent?: string;
  url?: string;
  userId?: string;
  timestamp: string;
}

class ErrorTracker {
  private static instance: ErrorTracker;
  private userId: string | null = null;
  private errorCount = 0;
  private lastErrorTime = 0;
  private maxErrorsPerMinute = 10;

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  setUserId(userId: string | null) {
    this.userId = userId;
  }

  async logError(error: Error, context?: string, errorInfo?: React.ErrorInfo) {
    // Rate limiting to prevent error spam
    const now = Date.now();
    if (now - this.lastErrorTime < 60000) {
      this.errorCount++;
      if (this.errorCount > this.maxErrorsPerMinute) {
        return; // Skip logging if too many errors
      }
    } else {
      this.errorCount = 1;
      this.lastErrorTime = now;
    }

    const errorEvent: ErrorEvent = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      } as Error,
      errorInfo,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.userId,
      timestamp: new Date().toISOString()
    };

    try {
      // Log to console only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error tracked:', errorEvent);
      }

      // Supabase logging temporarily disabled for stability
      // if (this.userId) {
      //   await supabase.from('error_logs').insert({...});
      // }
    } catch (loggingError) {
      // Silent failure - don't pollute console
    }
  }

  async logNetworkError(url: string, status: number, message: string) {
    try {
      await this.logError(
        new Error(`Network Error: ${status} - ${message}`),
        `Network request failed: ${url}`
      );
    } catch (error) {
      console.warn('Failed to log network error:', error);
    }
  }

  async logCrash(error: Error, errorInfo?: React.ErrorInfo) {
    await this.logError(error, 'App Crash', errorInfo);
  }
}

export const errorTracker = ErrorTracker.getInstance();